import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

/* WARNING: Do NOT import directly from "braintree-web", it causes the bundle size to increase dramatically. */
import client from "braintree-web/client";
import dataCollector from "braintree-web/data-collector";
import paypalCheckout from "braintree-web/paypal-checkout";
import venmo from "braintree-web/venmo";
import localPayment from "braintree-web/local-payment";
import { StoreState } from "../../store/store.interface";
import { ActionStatus } from "../../common/constants/action-status";
import { BraintreeState, DataCollectorRequest, LoadPaypalSdkRequest } from "./braintree.interface";
import { defaultApiState } from "../../common/constants/api-constants";
import { createApplePayInstance } from "./apple-pay/thunks/apple-pay-instance.thunk";
import { createGooglePayInstance } from "./google-pay/thunks/google-pay-instance.thunk";
import { createGooglePaymentClient } from "./google-pay/thunks/google-pay-client.thunk";

/**
 * Create braintree client only once.
 */
export const createClient = createAsyncThunk('braintree/createClient', async (_data: any, thunkAPI) => {
  try {
    return await client.create({
      authorization: _data.clientToken
    });
  } catch (error ) {
    return thunkAPI.rejectWithValue(error);
  }
}, {
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    if(state.braintreeState.braintreeClient.status === ActionStatus.loading || state.braintreeState.braintreeClient.status === ActionStatus.succeeded){
      return false;
    }
    return true;
  })
});


/**
 * Create braintree data collector only once.
 */
export const createDataCollector = createAsyncThunk('braintree/createDataCollector', async (_data: DataCollectorRequest, thunkAPI) => {
  try {
    await thunkAPI.dispatch(createClient(_data));
    const client = getBraintreeClient(thunkAPI.getState() as StoreState);
    return await dataCollector.create({
      client: client,
    });
  } catch (error ) {
    return thunkAPI.rejectWithValue(error);
  }
}, {
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    if(state.braintreeState.dataCollector.status === ActionStatus.loading || state.braintreeState.dataCollector.status === ActionStatus.succeeded){
      return false;
    }
    return true;
  })
});

/**
 * Create braintree paypal checkout instance only once.
 */
export const createPaypalCheckoutInstance = createAsyncThunk('braintree/createPaypalCheckoutInstance', async (_data: any, thunkAPI) => {
  try {
    await thunkAPI.dispatch(createClient(_data));
    return await paypalCheckout.create({
      client: getBraintreeClient(thunkAPI.getState() as StoreState),
      autoSetDataUserIdToken: _data.userAutoLogin
    });
  } catch (error ) {
    return thunkAPI.rejectWithValue(error);
  }
}, {
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    if(state.braintreeState.paypalCheckoutInstance.status === ActionStatus.loading || state.braintreeState.paypalCheckoutInstance.status === ActionStatus.succeeded){
      return false;
    }
    return true;
  })
});

/**
 * Load paypal SDK only once.
 */
export const loadPaypalSdk  = createAsyncThunk('braintree/loadPaypalSdk', async (_data: LoadPaypalSdkRequest, thunkAPI) => {
  try{
    await thunkAPI.dispatch(createPaypalCheckoutInstance(_data));
    return await getPaypalCheckoutInstance(thunkAPI.getState() as StoreState).loadPayPalSDK({
      components: 'buttons,messages',
      'enable-funding': _data["enable-funding"],
      currency: _data.currencyCode,
      intent: _data.intent,
      vault: true
    });
  } catch(error) {
    return thunkAPI.rejectWithValue(error);
  }
},{
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    if(state.braintreeState.loadPaypalSdk.status === ActionStatus.loading || state.braintreeState.loadPaypalSdk.status === ActionStatus.succeeded){
      return false;
    }
    return true;
  })
});
/**
 * Create braintree venmo instance only once.
 */
 export const createVenmoInstance = createAsyncThunk('braintree/createVenmoInstance', async (_data: any, thunkAPI) => {
  try {
    await thunkAPI.dispatch(createClient(_data));
    return await Promise.all([
      dataCollector.create({
        client: getBraintreeClient(thunkAPI.getState() as StoreState),
        paypal: true
      }),
       await venmo.create({
        client: getBraintreeClient(thunkAPI.getState() as StoreState),
        allowDesktop: true,
        paymentMethodUsage: 'multi_use',
        profileId:_data.profileId,
        
      })
    ]);
  } catch (error ) {
    return thunkAPI.rejectWithValue(error);
  }
}, {
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    if(state.braintreeState.venmoInstance.status === ActionStatus.loading || state.braintreeState.venmoInstance.status === ActionStatus.succeeded){
      return false;
    }
    return true;
  })
});

export const createLocalPaymentInstance = createAsyncThunk('braintree/createLocalPaymentInstance', async (_data: any, thunkAPI) => {
  try {
    await thunkAPI.dispatch(createClient(_data));
    const client = getBraintreeClient(thunkAPI.getState() as StoreState);

    if (!client) {
      throw new Error('Braintree client not initialized');
    }

    const { merchantAccountId } = _data;

    const config: any = {
      client,
    };

    if (merchantAccountId) {
      config.merchantAccountId = merchantAccountId;
    }

    return await localPayment.create(config);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
}, {
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    if(state.braintreeState.localPaymentInstance.status === ActionStatus.loading || state.braintreeState.localPaymentInstance.status === ActionStatus.succeeded){
      return false;
    }
    return true;
  })
});


/*----- CREATE INITIAL STATE ------*/

export const braintreeStateInitialState: BraintreeState = {
  braintreeClient: defaultApiState,
  dataCollector: defaultApiState,
  paypalCheckoutInstance: defaultApiState,
  loadPaypalSdk: defaultApiState,
  venmoInstance: defaultApiState,
  applePayInstance: defaultApiState,
  googlePayInstance: defaultApiState,
  googlePaymentClient: defaultApiState,
  isCreditCardFormLoaded: false,
  localPaymentInstance: defaultApiState,
};

/*----- CREATE STORE SLICE ------*/

export const braintreeSlice = createSlice({
  name:"braintree",
  initialState: braintreeStateInitialState,
  reducers:{
    setCreditCardFormLoadingState(state, action) {
      state.isCreditCardFormLoaded = action.payload;
    }
  },
  extraReducers(builder) {
    builder
    .addCase(createClient.pending, (state) => {
      state.braintreeClient.status = ActionStatus.loading;
    })
    .addCase(createClient.fulfilled, (state, action) => {
      state.braintreeClient.status = ActionStatus.succeeded;
      state.braintreeClient.data = action.payload;
    })
    .addCase(createClient.rejected, (state, action) => {
      state.braintreeClient.status = ActionStatus.failed;
      state.braintreeClient.error = action.payload;
    })
    .addCase(createDataCollector.pending, (state) => {
      state.dataCollector.status = ActionStatus.loading;
    })
    .addCase(createDataCollector.fulfilled, (state, action) => {
      state.dataCollector.status = ActionStatus.succeeded;
      state.dataCollector.data = action.payload;
    })
    .addCase(createDataCollector.rejected, (state, action) => {
      state.dataCollector.status = ActionStatus.failed;
      state.dataCollector.error = action.payload;
    })
    .addCase(createPaypalCheckoutInstance.pending, (state) => {
      state.paypalCheckoutInstance.status = ActionStatus.loading;
    })
    .addCase(createPaypalCheckoutInstance.fulfilled, (state, action) => {
      state.paypalCheckoutInstance.status = ActionStatus.succeeded;
      state.paypalCheckoutInstance.data = action.payload;
    })
    .addCase(createPaypalCheckoutInstance.rejected, (state, action) => {
      state.paypalCheckoutInstance.status = ActionStatus.failed;
      state.paypalCheckoutInstance.error = action.payload;
    })
    .addCase(loadPaypalSdk.pending, (state) => {
      state.loadPaypalSdk.status = ActionStatus.loading;
    })
    .addCase(loadPaypalSdk.fulfilled, (state, action) => {
      state.loadPaypalSdk.status = ActionStatus.succeeded;
      state.loadPaypalSdk.data = action.payload;
    })
    .addCase(loadPaypalSdk.rejected, (state, action) => {
      state.loadPaypalSdk.status = ActionStatus.failed;
      state.loadPaypalSdk.error = action.payload;
    })
    .addCase(createVenmoInstance.pending, (state) => {
      state.venmoInstance.status = ActionStatus.loading;
    })
    .addCase(createVenmoInstance.fulfilled, (state, action) => {
      state.venmoInstance.status = ActionStatus.succeeded;
      state.venmoInstance.data = action.payload;
    })
    .addCase(createVenmoInstance.rejected, (state, action) => {
      state.venmoInstance.status = ActionStatus.failed;
      state.venmoInstance.error = action.payload;
    })
    .addCase(createApplePayInstance.pending, (state) => {
      state.applePayInstance.status = ActionStatus.loading;
    })
    .addCase(createApplePayInstance.fulfilled, (state, action) => {
      state.applePayInstance.status = ActionStatus.succeeded;
      state.applePayInstance.data = action.payload;
    })
    .addCase(createApplePayInstance.rejected, (state, action) => {
      state.applePayInstance.status = ActionStatus.failed;
      state.applePayInstance.error = action.payload;
    })
    .addCase(createGooglePaymentClient.pending, (state) => {
      state.googlePaymentClient.status = ActionStatus.loading;
    })
    .addCase(createGooglePaymentClient.fulfilled, (state, action) => {
      state.googlePaymentClient.status = ActionStatus.succeeded;
      state.googlePaymentClient.data = action.payload;
    })
    .addCase(createGooglePaymentClient.rejected, (state, action) => {
      state.googlePaymentClient.status = ActionStatus.failed;
      state.googlePaymentClient.error = action.payload;
    })
    .addCase(createGooglePayInstance.pending, (state) => {
      state.googlePayInstance.status = ActionStatus.loading;
    })
    .addCase(createGooglePayInstance.fulfilled, (state, action) => {
      state.googlePayInstance.status = ActionStatus.succeeded;
      state.googlePayInstance.data = action.payload;
    })
    .addCase(createGooglePayInstance.rejected, (state, action) => {
      state.googlePayInstance.status = ActionStatus.failed;
      state.googlePayInstance.error = action.payload;
    })
    .addCase(createLocalPaymentInstance.pending, (state) => {
      state.localPaymentInstance.status = ActionStatus.loading;
    })
    .addCase(createLocalPaymentInstance.fulfilled, (state, action) => {
      state.localPaymentInstance.data = action.payload;
      state.localPaymentInstance.status = ActionStatus.succeeded;
    })
    .addCase(createLocalPaymentInstance.rejected, (state, action) => {
      state.localPaymentInstance.error = action.payload;
      state.localPaymentInstance.status = ActionStatus.failed;
    })
  }
});

/*----- CREATE SELECTORS ------*/

export const getBraintreeClient = (state: StoreState) =>
state.braintreeState.braintreeClient.data;

export const getBraintreeClientStatus = (state: StoreState) =>
state.braintreeState.braintreeClient.status;

export const getDataCollector = (state: StoreState) =>
state.braintreeState.dataCollector.data;

export const getDataCollectorStatus = (state: StoreState) =>
state.braintreeState.dataCollector.status;

export const getPaypalCheckoutInstance = (state: StoreState) =>
state.braintreeState.paypalCheckoutInstance.data;

export const getPaypalCheckoutInstanceStatus = (state: StoreState) =>
state.braintreeState.paypalCheckoutInstance.status;

export const getLoadPaypalSdkStatus = (state: StoreState) =>
state.braintreeState.loadPaypalSdk.status;

export const getVenmoInstance = (state: StoreState) =>
state.braintreeState.venmoInstance.data;

export const getVenmoInstanceStatus = (state: StoreState) =>
state.braintreeState.venmoInstance.status;

export const isCreditCardFormLoaded = (state: StoreState) => 
state.braintreeState.isCreditCardFormLoaded;

export const getLocalPaymentInstanceStatus = (state: StoreState) =>
state.braintreeState.localPaymentInstance.status;

export const getLocalPaymentInstance = (state: StoreState) =>
state.braintreeState.localPaymentInstance.data;

/*----- CREATE REDUCER ------*/

export const braintreeReducer = braintreeSlice.reducer;

export const { setCreditCardFormLoadingState } = braintreeSlice.actions;
